import * as React from 'react'
import styled, {css} from 'styled-components'
import {StyledDiv, StyledSpan} from '../../styledComponents/StyledTypeDefinitions'
import {Colors} from '../../styledComponents/Constants'
import {Nullable} from '../../app/TypeHelpers'
import {Box, Button, Link} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {FullWidthRowFlexContainer} from '../../styledComponents/FlexComponents'
import {StyledLink} from '../../styledComponents/adminPage/Links'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import {ROLE_FOR_ADMINISTRATOR} from '../../CodeMaster'
import {AccountCircle, AutorenewOutlined} from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'

const ADMIN_HEADER_Z_INDEX = 911
export const ADMIN_HEADER_HEIGHT = 60

export const AdminHeaderShadowCss = css`
  box-shadow: 0 2px 4px 0 rgba(29, 37, 84, 0.25);
`

export const AdminHeaderShadow = styled.div`
  position: relative;
  ${AdminHeaderShadowCss}
`

export const AdminHeaderArea: StyledDiv = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  box-sizing: border-box;
  color: ${Colors.Surface};
  background-color: ${Colors.AdminPrimaryVariant};
  position: relative;

  z-index: ${ADMIN_HEADER_Z_INDEX};
  height: ${ADMIN_HEADER_HEIGHT}px;
  line-height: 1.5;
`

const LogoText = styled.div`
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
`

const LogoSubText = styled.div`
  padding-left: 56px;
  font-size: 12px;
  line-height: 12px;
  white-space: nowrap;
`

export const AdminHeaderLogo = () => <>
    <div>
        <LogoText>
            素敵ネット
        </LogoText>
        <LogoSubText>
            管理用
        </LogoSubText>
    </div>
</>

const ModeBox = styled.div<{
    $borderColor: string
}>`
  padding: 4px;
  border: 1px solid ${props => props.$borderColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`

const ModeIcon = styled.img`
  padding: 1px 0 2px;
`

const ModeText = styled.div`
  font-size: 12px;
  line-height: 12px;
  white-space: nowrap;
`

const ChangeModeButton = styled(Button)`
  &.MuiButton-root {
    width: 150px;
    padding: 5px 0 6px;

    @media (hover: hover) {
      :hover {
        opacity: 0.8;
      }
    }
  }

  display: flex;
  flex-direction: column;
  align-items: center;
`

const HeaderCondominiumContainer: StyledDiv = styled.div`
  word-break: break-all;
  flex-grow: 1;
`

const ModeDetailsSubInformation = styled.div`
  margin-bottom: 4px;
  font-size: 12px;
  display: flex;
`

const ModeDetailsMainInformation = styled.div`
  font-size: 12px;
  font-weight: bold;
  display: flex;
`

function getModeImage(params: {
    allMode: boolean,
    spMode: boolean
}): JSX.Element {
    const alt = params.allMode ? '全体' : '物件'
    const src =
        params.allMode
            ? params.spMode
                ? '/resources/images/icons/icon_all_mode.svg'
                : '/resources/images/icons/icon_all_mode_white.svg'
            : params.spMode
                ? '/resources/images/icons/icon_condominium_mode.svg'
                : '/resources/images/icons/icon_condominium_mode_white.svg'
    return <img src={src} alt={alt}/>
}

const AdminHeaderModeIcon = (props: {
    allMode: boolean,
    spMode: boolean,
}) => {
    const modeImage = getModeImage({...props})
    const modeText = props.allMode ? '全体モード' : '物件モード'
    const borderColor = props.spMode ? 'rgba(112, 112, 112, 0.50)' : 'rgba(255, 255, 255, 0.50)'

    return <>
        <ModeBox $borderColor={borderColor}>
            {modeImage}
            <ModeText>{modeText}</ModeText>
        </ModeBox>
    </>
}

export const SelectedModeInfo = (props: {
    loginUserCondominiumGroupName: Nullable<string>,
    selectedCondominiumName: Nullable<string>,
    allMode: boolean,
    spMode: boolean,
    isBelongCondominiumGroup: boolean
}) => {
    if (!props.isBelongCondominiumGroup) {
        return <>
            <FullWidthRowFlexContainer>
                {getModeImage({allMode: false, spMode: props.spMode})}
                <Box width={'8px'}/>
                <EllipsisText>
                    {props.selectedCondominiumName}
                </EllipsisText>
            </FullWidthRowFlexContainer>
        </>
    }

    const subInformation =
        props.allMode
            ? props.spMode
                ? null
                : '物件グループ'
            : props.loginUserCondominiumGroupName
    const mainInformation =
        props.allMode
            ? props.loginUserCondominiumGroupName
            : props.selectedCondominiumName

    return <>
        <FullWidthRowFlexContainer>
            <AdminHeaderModeIcon {...props}/>
            <Box width={props.spMode ? '4px' : '16px'}/>
            <HeaderCondominiumContainer>
                {subInformation && <>
                    <ModeDetailsSubInformation>
                        <EllipsisText>
                            {subInformation}
                        </EllipsisText>
                    </ModeDetailsSubInformation>
                </>}
                <ModeDetailsMainInformation>
                    <EllipsisText>
                        {mainInformation}
                    </EllipsisText>
                </ModeDetailsMainInformation>
            </HeaderCondominiumContainer>
        </FullWidthRowFlexContainer>
    </>
}

export const ChangeModeButtons = (props: {
    allMode: boolean,
    handleClickAllMode: () => void,
    handleClickCondominiumMode: () => void,
}) => {
    const activeColorCss = {
        color: Colors.AccentDark,
        backgroundColor: 'rgb(226,229,244)',
    }
    const inactiveColorCss = {
        color: Colors.Surface,
        backgroundColor: Colors.AccentLight,
    }

    let allModeColorCss
    let allModeIcon
    let condominiumModeColorCss
    let condominiumModeIcon
    if (props.allMode) {
        allModeColorCss = activeColorCss
        allModeIcon = '/resources/images/icons/icon_all_mode.svg'
        condominiumModeColorCss = inactiveColorCss
        condominiumModeIcon = '/resources/images/icons/icon_condominium_mode_white.svg'
    } else {
        allModeColorCss = inactiveColorCss
        allModeIcon = '/resources/images/icons/icon_all_mode_white.svg'
        condominiumModeColorCss = activeColorCss
        condominiumModeIcon = '/resources/images/icons/icon_condominium_mode.svg'
    }

    const handleClickAllMode = () => {
        if (props.allMode) {
            return
        }
        props.handleClickAllMode()
    }

    return <>
        <Box display={'flex'} alignSelf={'center'}>
            <ChangeModeButton
                variant={'contained'}
                onClick={handleClickAllMode}
                style={allModeColorCss}
            >
                <ModeIcon src={allModeIcon} alt={'全体'}/>
                <ModeText>全体モード</ModeText>
            </ChangeModeButton>
            <Box width={'24px'}/>
            <ChangeModeButton
                variant={'contained'}
                onClick={props.handleClickCondominiumMode}
                style={condominiumModeColorCss}
            >
                <ModeIcon src={condominiumModeIcon} alt={'物件'}/>
                <ModeText>物件モード</ModeText>
            </ChangeModeButton>
        </Box>
    </>
}

export const PanelContainer = styled.div<{ $panelOpened: boolean }>`
  ${props => props.$panelOpened && `
    z-index: 1;
  `}
`

const PanelButton = styled(Button)<{
    $panelOpened: boolean
}>`
  &.MuiButton-root {
    height: ${ADMIN_HEADER_HEIGHT}px;
    padding: 0 8px;
    border-radius: 0;

    ${props => props.$panelOpened ? `
      color: ${Colors.AdminPrimaryVariant};
      background-color: ${Colors.Surface};
      @media(hover: hover) {
        :hover {
          background-color: ${Colors.Surface};
        }
      }
    ` : `
      color: ${Colors.Surface};
      background-color: ${Colors.AdminPrimaryVariant};
      @media(hover: hover) {
        :hover {
          background-color: ${Colors.AdminPrimaryVariant};
        }
      }
    `}
  }

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const PanelButtonText = styled.span`
  font-size: 12px;
  white-space: nowrap;
`

export const ChangeModePanelButton = (props: {
    panelOpened: boolean,
    onClick: () => void,
}) => <>
    <PanelButton
        $panelOpened={props.panelOpened}
        onClick={props.onClick}
        disableRipple={true}
    >
        <AutorenewOutlined/>
        <PanelButtonText>モード切替</PanelButtonText>
    </PanelButton>
</>

export const UserInfoPanelButton = (props: {
    panelOpened: boolean,
    onClick: () => void,
}) => <>
    <PanelButton
        $panelOpened={props.panelOpened}
        onClick={props.onClick}
        disableRipple={true}
    >
        <AccountCircle/>
        <PanelButtonText>ユーザ情報</PanelButtonText>
    </PanelButton>
</>

export const MenuPanelButton = (props: {
    panelOpened: boolean,
    onClick: () => void,
}) => <>
    <PanelButton
        $panelOpened={props.panelOpened}
        onClick={props.onClick}
        disableRipple={true}
    >
        <MenuIcon/>
        <PanelButtonText>メニュー</PanelButtonText>
    </PanelButton>
</>

const PanelItemTitle: StyledSpan = styled.span`
  font-size: 14px;
  color: ${Colors.AccentDark};
`

const PanelItemText: StyledSpan = styled.span`
  padding-top: 2px;
  font-size: 16px;
  word-break: break-all;
`

const SwitchingIcon = withStyles({
    root: {
        fontSize: '16px',
    },
})(AutorenewIcon)

export const ViewingCondominiumGroup = (props: {
    loginUserCondominiumGroupName: Nullable<string>,
    hasValidSubCondominiumGroup: boolean,
    handleClickCondominiumGroupSwitchingLink?: () => void,
}) => <>
    <Box height={'32px'}/>
    <FullWidthRowFlexContainer>
        <PanelItemTitle>
            物件グループ
        </PanelItemTitle>
        {props.hasValidSubCondominiumGroup &&
            <StyledLink onClick={props.handleClickCondominiumGroupSwitchingLink}>
                <SwitchingIcon/>
                <Box width={'4px'}/>
                切替
            </StyledLink>
        }
    </FullWidthRowFlexContainer>
    <PanelItemText>
        {props.loginUserCondominiumGroupName}
    </PanelItemText>
</>

export const ViewingCondominium = (props: {
    selectedCondominiumName: Nullable<string>,
    handleClickCondominiumSwitchingLink: () => void,
}) => <>
    <Box height={'32px'}/>
    <FullWidthRowFlexContainer>
        <PanelItemTitle>
            選択中の物件
        </PanelItemTitle>
        <StyledLink onClick={props.handleClickCondominiumSwitchingLink}>
            <SwitchingIcon/>
            <Box width={'4px'}/>
            切替
        </StyledLink>
    </FullWidthRowFlexContainer>
    <PanelItemText>
        {props.selectedCondominiumName}
    </PanelItemText>
</>

const ManualArea = styled.div`
  display: flex;
  flex-direction: column;
`

const PanelItemDescription = styled.span`
  padding-top: 2px;
  font-size: 12px;
  color: ${Colors.AccentDark};
`

export const ManualLink = (props: {
    roleId: Nullable<string>
}) => {
    const manualInfoList: {
        fileName: string
        role: string
    }[] = []

    if (props.roleId === ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value ||
        props.roleId === ROLE_FOR_ADMINISTRATOR.FRONT_STAFF.value
    ) {
        manualInfoList.push({
            fileName: 'AdminManualForFrontStaff.pdf',
            role: 'フロントスタッフ'
        })
    }

    if (props.roleId === ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value ||
        props.roleId === ROLE_FOR_ADMINISTRATOR.OFFICE_CENTER_OWNERSHIP_TRANSFER.value
    ) {
        manualInfoList.push({
            fileName: 'AdminManualForOfficeCenterOwnershipTransfer.pdf',
            role: '事務センター（重調担当）'
        })
    }

    if (props.roleId === ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value ||
        props.roleId === ROLE_FOR_ADMINISTRATOR.CALL_CENTER.value ||
        props.roleId === ROLE_FOR_ADMINISTRATOR.LIFE_MANAGER.value ||
        props.roleId === ROLE_FOR_ADMINISTRATOR.CONCIERGE.value
    ) {
        manualInfoList.push({
            fileName: 'AdminManualForLifeManager.pdf',
            role: 'ライフマネージャ'
        })
    }

    return <>
        {manualInfoList.map((manualInfo, index) =>
            <ManualArea key={index}>
                <Link
                    href={`/resources/contents/manual/${manualInfo.fileName}`}
                    target={'_blank'}
                    underline="hover">
                    素敵ネットマニュアル
                </Link>
                <PanelItemDescription>
                    {manualInfo.role}向けの操作マニュアルです
                </PanelItemDescription>
                <Box height={'24px'}/>
            </ManualArea>
        )}
    </>;
}

export const ResidentlLink = (props: {
    handleChangeLoginSiteToResidentButtonClick: () => void,
}) => {
    return <>
        <StyledLink onClick={props.handleChangeLoginSiteToResidentButtonClick}>
            素敵ネットへ
        </StyledLink>
        <PanelItemDescription>
            居住者様向けサービスに移動します
        </PanelItemDescription>
        <Box height={'32px'}/>
    </>
}

const UserName = styled(PanelItemText)`
  padding-top: 8px;
`

export const UserInfo = (props: {
    roleName: Nullable<string>,
    loginUserAdminFamilyName: Nullable<string>,
    loginUserAdminFirstName: Nullable<string>,
}) => <>
    <PanelItemTitle>
        {props.roleName}
    </PanelItemTitle>
    <UserName>
        {props.loginUserAdminFamilyName + ' ' + props.loginUserAdminFirstName}
    </UserName>
    <Box height={'32px'}/>
</>

const EllipsisText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 0;
  flex-grow: 1;
`

